import React, { useState, useCallback, useRef, useEffect } from 'react'

import { StyleSheet, Keyboard, Platform, View, ScrollView, Image, BackHandler, Alert, TouchableOpacity, Animated, ActivityIndicator } from 'react-native'
import { useFocusEffect } from '@react-navigation/native';

import AsyncStorage from '@react-native-async-storage/async-storage'

import { SafeAreaView } from 'react-native-safe-area-context'

import {
  Text,
  Title,
  Description,
  Space,
  Anchor,
  Button,
  TextInputExtended,
  KeyboardAvoidingView,
  ReturnFooter
} from '@sart/components'

import { useFormik } from 'formik'
import * as Yup from 'yup'

import client, { authenticationHelper } from '@sart/client'
import { useContent } from '@sart/content'
import { prettyErrorMessage, testID } from '@sart/utilities'
import { adaptive, backgroundColors, colors, typography } from '@sart/styles'
import { CompanyIcon, ChevronRight } from '@sart/icons'

import * as theme from './signTheme'

import AppConfig from '../Configuration'
import ApiClient from "../bff-client/src/ApiClient";
import CustomersApi from "../bff-client/src/api/CustomersApi";
import EmailApi from "../bff-client/src/api/EmailApi";

import delay from 'delay'

export default function SignIn(props) {
  const content = useContent()

  const { navigation } = props
  const [isRegistered, setIsRegistered] = useState(false)
  const [registering, setRegistering] = useState(false)
  const passwordRef = useRef(null)
  const emailRef = useRef(null)
  const fadeAnim = useRef(new Animated.Value(0)).current

  const registrationProgressSteps = [
    "🔴 Red - Waiting at the lights...",
    "🟡 Amber - Revving up the engine...",
    "🟢 Green - Go, go, go!",
    "🏁 Next destination: Your dashboard!"
  ]

  const [registrationProgress, setRegistrationProgress] = useState(registrationProgressSteps[0])

  useEffect(() => {
    if (isRegistered) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 500,
        useNativeDriver: true
      }).start()
    } else {
      fadeAnim.setValue(0)
    }
  }, [isRegistered])

  const bffClient = new ApiClient(AppConfig.bffBasePath)
  bffClient.defaultHeaders = {
    ...bffClient.defaultHeaders,
    Origin: AppConfig.shopOriginUrl
  }
  const customersApi = new CustomersApi(bffClient)
  const emailApi = new EmailApi(bffClient);

  useFocusEffect(
    useCallback(() => {
      const onBackPress = () => {
        Alert.alert(
          'Exit Biker Mobile',
          'Do you want to exit?',
          [
            {
              text: 'Cancel',
              onPress: () => { },
              style: 'cancel',
            },
            { text: 'YES', onPress: () => BackHandler.exitApp() },
          ],
          { cancelable: false }
        )

        return true
      }

      const backHandler = BackHandler.addEventListener('hardwareBackPress', onBackPress)

      return () => {
        // cleanup when screen loses focus
        if (backHandler && backHandler.remove) backHandler.remove()
        else BackHandler.removeEventListener('hardwareBackPress', onBackPress)
      }
    }, [])
  )


  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().trim().email(content.forms.emailInvalid).required(content.forms.required),
      password: Yup.string().trim().required(content.forms.passwordInvalid)
    }),
    onSubmit: handleSignIn
  })

  async function checkEmailAlreadyRegistered() {
    formik.setFieldError('email', null)
    formik.setFieldError('password', null)
    const email = formik.values.email.trim().toLowerCase()
    console.log('email', email)

    try {
      const subscriptionInfoResp = await customersApi.customersSubscriptionsPendingGet(email)
      console.log('subscriptionInfoResp is: ' + JSON.stringify(subscriptionInfoResp, null, 2))

      if (subscriptionInfoResp && subscriptionInfoResp.registered) {
        console.log('setting registered to true')
        setIsRegistered(true)
        passwordRef.current?.focus()
        formik.setFieldError('password', null)
      } else if (subscriptionInfoResp && !subscriptionInfoResp.registered) {
        //email exists in BFF but isn't registered
        setIsRegistered(false)
        if (subscriptionInfoResp.orderDate && new Date(subscriptionInfoResp.orderDate).toDateString() === new Date().toDateString()) {
          //Order was done today - no need to send an OTP
          //Begin registration - First set the password then capture remaining details 
          //(BFF/SSB calls held until the end so that the process can be restarted at any point)
          console.log('registering user as it is an eSIM journey performed today')
          const params = { email: email, number: subscriptionInfoResp.iccid, ncaFlow: false }
          navigation.navigate('SetPassword', params)

        } else {
          console.log('not same day so sending an OTP')
          await fireOtpAndRegister(email)
        }
      } else {
        console.log('Email does not exist')
        formik.setFieldError('email', content.register_number.emailDoesNotExist)
        formik.setFieldError('password', null)
        return
      }
    } catch (error) {
      console.log('checkEmailAlreadyRegistered error is: ' + JSON.stringify(error, null, 2))
      formik.setFieldError('email', content.register_number.emailDoesNotExist)
      formik.setFieldTouched('email', true, false)
      formik.setFieldError('password', null)
      setIsRegistered(false)
    }
  }

  async function registerWithoutOTP(email, iccid) {
    setRegistrationProgress(registrationProgressSteps[0])
    setRegistering(true)

    let registrationTimeout1 = setTimeout(() => {
      setRegistrationProgress(registrationProgressSteps[1])
    }, 6000)

    let registrationTimeout2 = setTimeout(() => {
      setRegistrationProgress(registrationProgressSteps[2])
    }, 12000)

    try {

      // await delay(7000)
      await client.registration.registerUser({
        username: email,
        primaryIdentifier: iccid,
        operatorCode: AppConfig.operatorCode,
        dob: '1991-03-03',
        //TODO This should be captured at the front end. Especially if there are different codes for different regions.
        twoFactorAuthNumber: '07506736598',
        twoFactorAuthNumberCountryCode: "+44",
        emailAddress: email,
        mockOtpSend: true
      })
      //TODO - we need to rework this after the demo
      console.log('signing in with auth code')
      const auth = await client.signInWithVerificationCode({ email: email, code: '222222' })
      console.log('getting user')
      const user = await client.callApi('user', 'getUser', [auth.userId])

      user.regCompleted = true
      user.regCompletedDate = null
      console.log('updating user')
      await client.callApi('user', 'modifyUser', [user.userId, user])

      const newUser = await client.callApi('user', 'getUser', [user.userId])

      console.log('AFTER REGISTRATION USER IS ' + JSON.stringify(newUser, null, 2));

      //stop the setTimouts and let the progress get picked up by the below login in the for loop
      clearTimeout(registrationTimeout1)
      clearTimeout(registrationTimeout2)

      // const currentRegistrationProgress = registrationProgressSteps.indexOf(registrationProgress)
      // for (let i = currentRegistrationProgress + 1; i < registrationProgressSteps.length; i++) {
      //   setRegistrationProgress(registrationProgressSteps[i])
      //   await delay(3000)
      // }

      const params = { ...auth, email: email, number: iccid, ncaFlow: false }

      navigation.navigate('SetPassword', params)


    } catch (error) {
      console.log('registerWithoutOTP error is: ' + JSON.stringify(error))
      setRegistrationProgress(registrationProgressSteps[0])
    } finally {
      setRegistering(false)
    }
  }

  async function fireOtpAndRegister(email) {

    //Send OTP
    console.log('sending OTP')
    const validationResp = await emailApi.emailVerifyPost({ email: email, register: true });
    console.log('validationResp is: ' + JSON.stringify(validationResp, null, 2))
    const params = { email: email }
    //OTP sent
    navigation.navigate('VerifyNumber', params)
  }

  async function handleSignIn(values, form) {
    Keyboard.dismiss()
    form.setSubmitting(true)

    values.username = values.email.trim().toLowerCase()

    try {
      const auth = await client.signInWithMobileAndPassword(values)
      const user = await client.callApi('user', 'getUser', [auth.userId])

      await AsyncStorage.setItem('lastLoginDate', new Date().toDateString())

      navigation.navigate('Main', {
        screen: "Dashboard",
        params: {/*syncAccounts:true*/ }
      })
    } catch (error) {
      console.log(error)
      if (error.response && error.response.body &&
        (error.response.body.messageCode === "SR0015" || error.response.body.code === 'MUST_CHANGE_PASSOWRD')) {
        //if must change password, redirect to password change screen
        await handleMustChangePassword(values, form)

      } else if (error.response && error.response.body &&
        (error.response.body.messageCode === "SR00151" || error.response.body.code === 'REGISTRATION_INCOMPLETE')) {
        //if reg is not completed, redirect to the register details screen
        await handleMustCompleteRegistration(values, form)

      } else {
        const message = prettyErrorMessage(error, 'login', content)
        form.setFieldError('password', message)
        form.setSubmitting(false)

      }
    }
  }

  async function handleMustCompleteRegistration(values, form) {
    client.setupHeaderBasicAuth({ username: values.email, password: values.password })
    await client.storeBasicAuthInformation({ username: values.email, password: values.password })
    let basicAuth = await client.retriveStoredBasicAuthInformation()

    const auth = await client.callApi('authentication', 'createToken')
    await client.storeAuthInformation(auth)
    await client.cacheAuthInformation(auth)
    client.setupHeaderBearerAuth(client.cache.token)
    basicAuth = await client.retriveStoredBasicAuthInformation()

    const user = await client.callApi('user', 'getUser', [auth.userId])

    const params = {
      ...auth,
      userId: auth.userId,
      customerId: auth.customerId,
      number: user.capturedRegistrationDetails.twoFactorAuthNumber,
      email: user.userName
    }
    navigation.navigate('RegisterDetails', params)
  }

  async function handleMustChangePassword(values, form) {
    client.setupHeaderBasicAuth({ username: values.email, password: values.password })
    await client.storeBasicAuthInformation({ username: values.email, password: values.password })

    const auth = await client.callApi('authentication', 'createToken')
    await client.storeAuthInformation(auth)
    await client.cacheAuthInformation(auth)
    client.setupHeaderBearerAuth(client.cache.token)

    const user = await client.callApi('user', 'getUser', [auth.userId])

    const params = {
      ...auth,
      userId: auth.userId,
      emailAddress: user.userName
    }
    navigation.navigate('ForgottenPasswordNewPassword', params)
  }
  return (
    <SafeAreaView style={styles.container} >
      <ScrollView style={styles.scrollViewContainer} keyboardShouldPersistTaps={'always'}>
        <KeyboardAvoidingView style={styles.content}>
          <Space height={styles.spaceHeightTop.height} />
          <View style={styles.logo} >
            <Image
              source={{ uri: 'https://shop.bikermobile.com/assets/biker_mobile_logo_final_nobg.png' }}
              //height={styles.logoStyle.height} width={200} alignSelf={"center"}
              style={styles.logoImage}
            />
            {/* <CompanyIcon color={colors.white} height={styles.logoStyle.height} width={200} alignSelf={"center"} /> */}
          </View>
          {/* <Title {...theme.title}>{content.login.title}</Title> */}

          <Space height={12} />

          <Description {...theme.description}>
            {content.login.instructions}
          </Description>

          <Space height={styles.spaceHeightTwo.height} />

          <View style={{ flexDirection: 'row', alignItems: 'center' }}>
            <View style={{ flex: 1 }}>
              <TextInputExtended
                label={content.forms.emailLabel}
                value={formik.values.email}
                placeholder={content.forms.emailPlaceholder}
                keyboardType="email-address"
                {...Platform.select({
                  web: { autoComplete: 'email' },
                  ios: { textContentType: 'emailAddress' },
                  android: { autoCompleteType: 'email' }
                })}
                autoCapitalize="none"
                onChangeText={(email) => {
                  setIsRegistered(false)
                  formik.setFieldValue('email', email)
                }
                }
                ref={emailRef}
                onBlur={formik.handleBlur('email')}
                error={formik.touched.email && formik.errors.email}
                {...theme.textInput}
                returnKeyType="next"
                onSubmitEditing={checkEmailAlreadyRegistered}
                {...testID('email')}
              />
            </View>
            <TouchableOpacity onPress={checkEmailAlreadyRegistered} style={{ marginLeft: 10, marginTop: 28 }}>
              <ChevronRight color={colors.white} size={30} />
            </TouchableOpacity>
          </View>
          {registering &&
            <>
              <Space height={16} />
              <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'center' }}>
                {/* <Text>{registrationProgress}</Text>
                <Space width={16} /> */}
                <ActivityIndicator color={colors.brightOrange} size={40} />
              </View>
            </>
          }
          {isRegistered &&
            <Animated.View style={{ opacity: fadeAnim }}>
              <Space height={8} />

              <TextInputExtended
                allowShowHidePassword={true}
                label={content.forms.passwordLabel}
                {...Platform.select({
                  web: { autoComplete: 'password' },
                  ios: { textContentType: 'password' },
                  android: { autoCompleteType: 'password' }
                })}
                placeholder={content.forms.passwordPlaceholder}
                value={formik.values.password}
                onChangeText={formik.handleChange('password')}
                onBlur={formik.handleBlur('password')}
                showErrorAndFooter
                ref={passwordRef}
                returnKeyType="go"
                error={formik.touched.password && formik.errors.password}
                {...theme.textInput}
                onSubmitEditing={formik.handleSubmit}
                {...testID('password')}
              />

              <Space height={24} />

              <Button
                title={
                  formik.isSubmitting
                    ? content.login.inProgress
                    : content.login.action
                }
                disabled={!formik.dirty || !formik.isValid || formik.isSubmitting}
                onPress={formik.handleSubmit}
                {...theme.button}
                titleStyle={styles.enabledTitleStyle}
                disabledTitleStyle={styles.disabledTitleStyle}
              />

              <Space height={24} />

              <Text style={styles.description}>
                <Anchor
                  title={content.login.forgottenPassword}
                  onPress={() => navigation.navigate('ForgottenPassword')}
                  style={styles.link}
                  {...testID('forgottenPasswordAnchor')}
                />
              </Text>
            </Animated.View>
          }

        </KeyboardAvoidingView>
      </ScrollView>
    </SafeAreaView>
  )
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: backgroundColors.darkCharcoal,
    paddingTop: 0,
    width: '100%',
    alignItems: 'center'
  },
  scrollViewContainer: {
    flex: 1,
    backgroundColor: backgroundColors.darkCharcoal,
    width: '100%',
    maxWidth: 600
  },
  content: {
    flex: 1,
    ...adaptive.wrapperPadding,
    // justifyContent: 'center',
    ...adaptive.loginContentHeight
  },
  description: {
    textAlign: 'center',
    ...typography.demi,
    color: colors.brightOrange
  },
  link: {
    color: colors.clear,
    fontSize: 18,
    ...typography.extrabold
  },
  logo: {
    alignSelf: 'center'
  },
  forgottenPassword: {
    color: colors.white
  },
  spaceHeightOne: {
    ...adaptive.spaceHeightLoginScreenOne
  },
  spaceHeightTwo: {
    ...adaptive.spaceHeightLoginScreenTwo
  },
  spaceHeightTop: {
    ...adaptive.spaceHeightLoginScreenTop
  },
  logoStyle: {
    ...adaptive.guruSize
  },
  cancelStyle: {
    color: colors.brightOrange
  },
  enabledTitleStyle: {
    fontSize: 18,
    color: colors.white,
    ...typography.extrabold
  },
  disabledTitleStyle: {
    fontSize: 18,
    color: colors.white + 45,
    ...typography.extrabold
  },
  icon: {
    marginLeft: 10,
  },
  logoImage: {
    height: 45,
    width: 45,
    position: 'relative',
    alignSelf: 'center',
    width: 200,
    ...adaptive.guruSize
  }
})
